import { useMutation, useQuery } from '@tanstack/react-query';
import { saveFile } from '@tf/utils';

import { useConfig } from '@/core/providers';
import { useOriginTenantSlug } from '@/core/providers/OriginTenantSlugProvider';

/**
 * Rich error thrown when a case request API call fails. Carries the HTTP status,
 * response body and correlation id so the failure can be investigated in Sentry /
 * traefik / CrowdSec logs instead of being collapsed into a generic message.
 */
export class CaseRequestApiError extends Error {
	readonly status: number;
	readonly statusText: string;
	readonly requestId: string | null;
	readonly responseBody: string;

	constructor(params: {
		status: number;
		statusText: string;
		requestId: string | null;
		responseBody: string;
	}) {
		super(`Case request API error ${params.status} ${params.statusText || ''}`.trim());
		this.name = 'CaseRequestApiError';
		this.status = params.status;
		this.statusText = params.statusText;
		this.requestId = params.requestId;
		this.responseBody = params.responseBody;
	}

	static async fromResponse(res: Response): Promise<CaseRequestApiError> {
		// Read the body defensively - it may be empty, already consumed, or non-text.
		let responseBody = '';
		try {
			responseBody = await res.text();
		} catch {
			// ignore - body unavailable
		}
		// traefik / otel propagate a correlation id in the response headers; capturing it
		// lets us jump straight to the matching traefik + CrowdSec AppSec log lines.
		const requestId =
			res.headers.get('x-request-id') ?? res.headers.get('x-trace-id') ?? res.headers.get('traceresponse') ?? null;

		return new CaseRequestApiError({
			status: res.status,
			statusText: res.statusText,
			requestId,
			responseBody: responseBody.slice(0, 2000),
		});
	}
}

// Our codegen API client doesn't support multi file upload, so we use fetch here
// See https://github.com/acacode/swagger-typescript-api/issues/965
const useCaseRequestsAPIClient = () => {
	const config = useConfig();
	const originTenantSlug = useOriginTenantSlug();

	return async (path: string, init?: RequestInit) => {
		const res = await fetch(`${config.urls.api}/v1${path}`, {
			...init,
			headers: {
				...init?.headers,
				'Origin-Tenant-Slug': originTenantSlug,
			},
		});

		if (!res.ok) {
			throw await CaseRequestApiError.fromResponse(res);
		}

		return res;
	};
};

export type CaseRequestFile = {
	id: string;
	filename: string;
	mime: string;
	size: number;
};

export type InformationRequest = {
	id: string;
	caseId: string;
	caseKind: 'information_request';
	status: 'SENT';
	dueDate?: string;
	requestText?: string;
	requestFiles?: CaseRequestFile[];
};

export type AdditionalDocumentsRequest = {
	id: string;
	caseId: string;
	caseKind: 'additional_documents';
	status: 'SENT';
	dueDate?: string;
	requestText?: string;
	requestFiles?: CaseRequestFile[];
};

export type IdentityVerificationRequest = {
	id: string;
	caseId: string;
	caseKind: 'identity_verification';
	status: 'SENT';
	dueDate?: string;
	payload: {
		serviceKind: 'idenfy';
		webToken: string;
	};
};

type InactiveCaseRequest = { id: string; status: 'UPDATED' | 'REVOKED' };

export type CaseRequest =
	| InformationRequest
	| AdditionalDocumentsRequest
	| IdentityVerificationRequest
	| InactiveCaseRequest;

export const useCaseRequest = (requestObjectName: string) => {
	const api = useCaseRequestsAPIClient();

	return useQuery({
		queryKey: ['caseRequest', requestObjectName],
		queryFn: async (): Promise<CaseRequest> => {
			const res = await api(`/cases/requests/${requestObjectName}`, {
				method: 'GET',
			});
			return res.json();
		},
	});
};

export const useSubmitCaseRequestMutation = () => {
	const api = useCaseRequestsAPIClient();

	return useMutation({
		mutationFn: async ({
			id,
			responseText,
			files,
		}: {
			id: string;
			responseText: string;
			files: File[];
		}): Promise<InactiveCaseRequest> => {
			const formData = new FormData();
			formData.append('responseText', responseText);
			files.forEach((file) => {
				formData.append('files', file);
			});

			const res = await api(`/cases/requests/${id}`, {
				method: 'POST',
				body: formData,
			});
			return res.json();
		},
	});
};

export const useDownloadCaseRequestFileMutation = () => {
	const api = useCaseRequestsAPIClient();

	return useMutation({
		mutationFn: async ({ requestId, file }: { requestId: string; file: CaseRequestFile }) => {
			const res = await api(`/cases/requests/${requestId}/files/${file.id}`, {
				method: 'GET',
			});
			saveFile(await res.blob(), file.filename);
		},
	});
};
